// Firma yetkilisi: belgelerini görüntüleme/yeniden yükleme ve reddedilen başvuruyu tekrar gönderme
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { DocumentUploadButton } from '../employees/DocumentUploadButton';
import { DocumentViewerDialog } from '../employees/DocumentViewerDialog';
import { RotateCcw, Loader2, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FIRM_DOCS = [
  { key: 'kvkk_taahhutnamesi', label: 'KVKK Taahhütnamesi' },
  { key: 'aydinlatma_yukumlulugu', label: 'Aydınlatma Yükümlülüğü', onlyIf: (c) => c.aydinlatma_required || c.company_type === 'magaza' || c.aydinlatma_renewal || c.document_files?.aydinlatma_yukumlulugu },
  { key: 'vergi_levhasi', label: 'Vergi Levhası' },
  { key: 'ust_isveren_vekili', label: 'Üst İşveren Vekili', optional: true },
];

const DOC_STATE = {
  onay: { label: 'Onaylandı', cls: 'text-emerald-700 bg-emerald-50' },
  eksik: { label: 'Reddedildi', cls: 'text-rose-700 bg-rose-50' },
  beklemede: { label: 'Onay Bekliyor', cls: 'text-amber-700 bg-amber-50' },
};

const FirmDocRow = ({ company, doc: d, onChanged }) => {
  const [viewing, setViewing] = useState(false);
  const hasFile = !!company.document_files?.[d.key];
  const status = hasFile ? (company.documents?.[d.key] || 'beklemede') : null;
  const st = DOC_STATE[status];
  const note = company.documents?.[`${d.key}_note`];
  const validity = company.documents?.[`${d.key}_validity`];
  const daysLeft = validity ? Math.round((Date.parse(validity.slice(0, 10)) - Date.parse(new Date().toLocaleDateString('sv-SE'))) / 86400000) : null;
  const expiring = status === 'onay' && daysLeft !== null && daysLeft <= 30;
  const uploadLabel = !hasFile ? 'Belge Yükle' : status === 'eksik' ? 'Yeni Belge Yükle' : expiring ? 'Güncel Belge Yükle' : null;
  return (
    <div className={`rounded-lg border p-2.5 space-y-1.5 ${status === 'eksik' ? 'border-rose-300' : ''}`} data-testid={`firm-doc-${d.key}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-slate-700">{d.label}</span>
        {st ? <span className={`text-[10px] px-1.5 py-0.5 rounded ${st.cls}`} data-testid={`firm-doc-status-${d.key}`}>{st.label}</span> : <span className="text-[10px] text-slate-400" data-testid={`firm-doc-status-${d.key}`}>Yüklenmedi{d.optional ? ' (Opsiyonel)' : ''}</span>}
      </div>
      {validity && status === 'onay' && (
        <p className={`text-[11px] ${expiring ? 'text-orange-600 font-medium' : 'text-slate-500'}`} data-testid={`firm-doc-validity-${d.key}`}>
          Geçerlilik: {validity.split('-').reverse().join('.')}{expiring ? (daysLeft < 0 ? ' · Süresi doldu' : ` · ${daysLeft} gün kaldı`) : ''}
        </p>
      )}
      {status === 'eksik' && note && <p className="text-[11px] text-rose-600" data-testid={`firm-doc-note-${d.key}`}>Red nedeni: {note}</p>}
      <div className="flex flex-col gap-1.5">
        {hasFile && (
          <button type="button" onClick={() => setViewing(true)} className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded border border-slate-200 text-slate-700 hover:bg-slate-50" data-testid={`firm-doc-view-${d.key}`}>
            <Eye className="h-3.5 w-3.5" />Belgeyi Görüntüle
          </button>
        )}
        {uploadLabel && (
          <div className="flex"><DocumentUploadButton entityType="companies" entityId={company.id} docKey={d.key} hasFile={hasFile} onUploaded={onChanged} label={uploadLabel} /></div>
        )}
      </div>
      <DocumentViewerDialog open={viewing} onOpenChange={setViewing} entity={company} entityType="companies" subtitle={company.name} docKey={d.key} docLabel={d.label} mode={status === 'onay' ? 'approved' : 'latest'} />
    </div>
  );
};

export const FirmDocuments = ({ company, onChanged }) => (
  <div className="space-y-2" data-testid="firm-documents">
    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Belgelerim</p>
    {FIRM_DOCS.filter(d => !d.onlyIf || d.onlyIf(company)).map(d => <FirmDocRow key={d.key} company={company} doc={d} onChanged={onChanged} />)}
  </div>
);

const FIELDS = [
  { key: 'trade_name', label: 'Ticari Unvan' },
  { key: 'name', label: 'Firma Adı' },
  { key: 'sgk_sicil_no', label: 'SGK Sicil No' },
  { key: 'nace_kodu', label: 'NACE Kodu' },
  { key: 'activity_description', label: 'Faaliyet / İş Tanımı' },
  { key: 'contact_name', label: 'Yetkili Adı' },
  { key: 'contact_surname', label: 'Yetkili Soyadı' },
  { key: 'contact_phone', label: 'Telefon' },
];

export const ResubmitButton = ({ company, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  const start = () => { setValues(Object.fromEntries(FIELDS.map(f => [f.key, company[f.key] || '']))); setOpen(true); };
  const missing = FIELDS.filter(f => !values[f.key]?.trim()).map(f => f.key);

  const submit = async () => {
    if (missing.length) { toast.error('Kırmızı ile işaretli alanları doldurun'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/my-company/resubmit`, values);
      toast.success('Başvurunuz yeniden onaya gönderildi');
      setOpen(false);
      onChanged();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Gönderilemedi');
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button size="sm" onClick={start} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="firm-resubmit-btn">
        <RotateCcw className="h-3.5 w-3.5 mr-1" />Bilgileri Düzelt ve Tekrar Gönder
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="firm-resubmit-dialog" data-allow-pending>
          <DialogHeader><DialogTitle>Başvuruyu Düzelt ve Tekrar Gönder</DialogTitle></DialogHeader>
          {company.registration_reason && <p className="text-sm rounded-lg bg-rose-50 border border-rose-200 text-rose-700 p-3">Red sebebi: {company.registration_reason}</p>}
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(f => (
              <div key={f.key} className={f.key === 'trade_name' ? 'sm:col-span-2' : ''}>
                <Label>{f.label} <span className="text-rose-500">*</span></Label>
                <Input value={values[f.key] || ''} onChange={(e) => setValues(v => ({ ...v, [f.key]: e.target.value }))} className={`mt-1 ${missing.includes(f.key) ? 'border-rose-500' : ''}`} data-testid={`firm-resubmit-${f.key}`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">Reddedilen belgelerinizi "Belgelerim" bölümünden yeniden yükleyebilirsiniz.</p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Vazgeç</Button>
            <Button onClick={submit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="firm-resubmit-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Tekrar Onaya Gönder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
